import React, { useState } from "react";
import { JWKS_URL } from "../lib/config";
import { CHECK_KINDS, toCanonical } from "../lib/identifiers";
import StatusBadge, { PUBLIC_STATUS_TONE } from "../components/brand/StatusBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const truncate = (s, n) => (s.length > n ? `${s.slice(0, n)}…` : s);
// Why a "malicious" answer is malicious (Decision 32).
const BASIS_LABEL = { human_review: "Human review", automated: "Automated (two independent models agreed)" };
const TYPE_LABEL = { mcp_server: "MCP server", mcp_remote: "Remote MCP server", package: "Package",
  x402_endpoint: "x402 endpoint", url: "URL", domain: "Domain", manual: "Submitted item" };
const day = (iso) => (iso ? String(iso).slice(0, 10) : "");
const requestScanHref = (identifier) => `/report?scan=1&server=${encodeURIComponent(identifier || "")}`;

// "unknown" means two different things; say which (older responses without `known` keep the raw status).
const statusLabel = (r) => {
  if (r.status !== "unknown" || r.known === undefined) return r.status;
  if (!r.known) return "not in HerdSafe yet";
  return r.scanned ? "scanned" : "listed";
};

// The "do we have a record?" line (backend services/public_record.py). Older responses carry no `known` field:
// they show the status badge only, as before.
function RecordLine({ result }) {
  if (result.status !== "unknown" || result.known === undefined) return null;
  if (!result.known) {
    return (
      <div data-testid="check-result-record" className="text-sm">
        <strong>Not in HerdSafe yet.</strong> We have no record of this item.{" "}
        <a data-testid="check-request-scan" className="hs-link" href={requestScanHref(result.identifier)}>Request a scan</a>
      </div>
    );
  }
  if (!result.scanned) {
    return <div data-testid="check-result-record" className="text-sm"><strong>Listed in HerdSafe, not scanned yet.</strong></div>;
  }
  return (
    <div data-testid="check-result-record" className="text-sm">
      <strong>Scanned {day(result.scanned_at)} — no confirmed threats.</strong>
    </div>
  );
}

// Plain-English summary + caution badges with one-line explanations. All of it is text (never markup).
function RecordDetails({ result }) {
  const details = result.caution_details || [];
  const m = result.match;
  const matchedOther = m && (m.identifier !== result.identifier || m.via !== "exact");
  return (
    <>
      {matchedOther && (
        <div data-testid="check-result-match" className="text-sm text-muted">
          Matched: {TYPE_LABEL[m.type] || m.type} <span className="hs-mono break-all">{m.identifier}</span>
        </div>
      )}
      {result.summary && (
        <div>
          <div className="hs-label">What this is</div>
          <p data-testid="check-result-summary" className="m-0 hs-prose">{result.summary}</p>
        </div>
      )}
      {details.length > 0 && (
        <div>
          <div className="hs-label">Caution</div>
          <ul data-testid="check-result-caution" className="m-0 pl-0 list-none space-y-1 text-sm">
            {details.map((d) => (
              <li key={d.id} data-testid={`check-caution-${d.id}`}>
                <StatusBadge tone="warning">{d.label || d.id}</StatusBadge>{" "}
                <span>{d.explanation}</span>
              </li>
            ))}
          </ul>
          <div className="hs-hint mt-1">Informational: caution badges are facts worth knowing, not findings of a threat.</div>
        </div>
      )}
    </>
  );
}

// Name / domain search: a short list of items we know ("rokha", "rokha.ai").
function NameSearch({ onPick }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    setErr(""); setRows(null); setBusy(true);
    try {
      const res = await fetch(`${API}/public/search?q=${encodeURIComponent(q.trim())}`);
      const body = await res.json();
      if (!res.ok) setErr(body.detail || `HTTP ${res.status}`);
      else setRows(body.results || []);
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={run} data-testid="search-form" className="hs-card max-w-2xl space-y-3">
      <div>
        <label className="hs-label" htmlFor="search-q">Find by name or domain</label>
        <input id="search-q" data-testid="search-q" required minLength={3} className="hs-input hs-mono"
               placeholder="rokha.ai" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <button data-testid="search-submit" type="submit" disabled={busy} className="hs-btn">{busy ? "Searching…" : "Search"}</button>
      {err && <div role="alert" className="hs-text-danger">{err}</div>}
      {rows && rows.length === 0 && (
        <div data-testid="search-empty" className="text-sm">
          Nothing in HerdSafe matches. <a className="hs-link" href={requestScanHref(q.trim())}>Request a scan</a>
        </div>
      )}
      {rows && rows.length > 0 && (
        <ul data-testid="search-results" className="m-0 pl-0 list-none space-y-1 text-sm">
          {rows.map((r) => (
            <li key={`${r.type}:${r.identifier}`}>
              <button type="button" className="hs-link text-left" data-testid="search-result"
                      onClick={() => onPick({ type: r.type, identifier: r.identifier })}>
                <span className="hs-mono break-all">{r.name}</span>
              </button>{" "}
              <span className="text-muted">
                {TYPE_LABEL[r.type] || r.type}{r.scanned ? ` · scanned ${day(r.scanned_at)}` : " · not scanned yet"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </form>
  );
}

export default function Check() {
  const [type, setType] = useState("npm");
  const [identifier, setIdentifier] = useState("");
  const [result, setResult] = useState(null);
  const [signature, setSignature] = useState("");
  const [keyId, setKeyId] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    return check(toCanonical(type, identifier));
  };

  const check = async (payload) => {
    setErr(""); setResult(null); setSignature(""); setKeyId(""); setLoading(true);
    try {
      const res = await fetch(`${API}/public/check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      // x-ato-*: legacy header names, deprecated, removal in 0.5.
      const sig = res.headers.get("x-herdsafe-signature") || res.headers.get("x-ato-signature") || "";
      const kid = res.headers.get("x-herdsafe-key-id") || res.headers.get("x-ato-key-id") || "";
      const body = await res.json();
      if (!res.ok) {
        setErr(body.detail || `HTTP ${res.status}`);
      } else {
        setResult(body); setSignature(sig); setKeyId(kid);
      }
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setLoading(false);
    }
  };

  const hint = CHECK_KINDS.find(k => k.value === type)?.hint;
  return (
    <section data-testid="check-page" className="space-y-6">
      <h1 className="hs-display text-3xl md:text-4xl">Check an item</h1>
      <p className="text-muted hs-prose">
        Confirmed-only lookup. Suspected items are never surfaced through this endpoint;
        responses are signed with the HerdSafe <code className="hs-mono text-sm">ed25519</code> key
        exposed at <code className="hs-mono text-sm break-all">{JWKS_URL}</code>.
        A URL is matched however it is written and whichever item type you pick.
      </p>
      <form onSubmit={submit} data-testid="check-form" className="hs-card max-w-2xl space-y-4">
        <div>
          <label className="hs-label" htmlFor="check-type">Item type</label>
          <select id="check-type" data-testid="check-type" className="hs-input"
                  value={type} onChange={e => setType(e.target.value)}>
            {CHECK_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
        </div>
        <div>
          <label className="hs-label" htmlFor="check-identifier">Identifier</label>
          <input id="check-identifier" data-testid="check-identifier" required className="hs-input hs-mono"
                 placeholder={hint} aria-describedby={hint ? "check-identifier-hint" : undefined}
                 value={identifier} onChange={e => setIdentifier(e.target.value)} />
          {hint && <div id="check-identifier-hint" className="hs-hint mt-1">Example: <span className="hs-mono">{hint}</span></div>}
        </div>
        <button data-testid="check-submit" type="submit" disabled={loading} className="hs-btn hs-btn-primary">
          {loading ? "Checking…" : "Check"}
        </button>
        {err && <div data-testid="check-error" role="alert" className="hs-text-danger">{err}</div>}
      </form>
      <NameSearch onPick={check} />
      {result && (
        <div data-testid="check-result-card" className="hs-card max-w-2xl space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge testid="check-result-status" tone={PUBLIC_STATUS_TONE[result.status] || "neutral"}>
              {statusLabel(result)}
            </StatusBadge>
            <span className="hs-mono text-sm break-all">{result.type}·{result.identifier}</span>
          </div>
          <RecordLine result={result} />
          <RecordDetails result={result} />
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
            <dt className="text-muted">Tier</dt><dd data-testid="check-result-tier" className="m-0">{result.tier || "—"}</dd>
            {result.basis && (<>
              <dt className="text-muted">Basis</dt>
              <dd data-testid="check-result-basis" className="m-0">
                {BASIS_LABEL[result.basis] || result.basis}{" "}
                <a href="/methodology#automated-confirmation" className="hs-link">how</a>
              </dd>
            </>)}
            <dt className="text-muted">Techniques</dt>
            <dd data-testid="check-result-techniques" className="m-0">
              {(result.techniques || []).join(", ") || "—"}
            </dd>
            <dt className="text-muted">First seen</dt><dd className="m-0">{result.first_seen || "—"}</dd>
            <dt className="text-muted">Last seen</dt><dd className="m-0">{result.last_seen || "—"}</dd>
            <dt className="text-muted">Report</dt>
            <dd className="m-0">{result.report_url
              ? <a href={result.report_url} className="hs-link">view</a>
              : "—"}</dd>
          </dl>
          <div className="pt-3 text-xs text-muted border-t border-border">
            <span className="hs-mono">X-HerdSafe-Signature:</span>{" "}
            <code data-testid="check-signature" className="hs-mono break-all text-fg" title={signature}>
              {truncate(signature, 96) || "—"}
            </code>
            <br />
            <span className="hs-mono">X-HerdSafe-Key-Id:</span>{" "}
            <code data-testid="check-key-id" className="hs-mono break-all text-fg">{keyId || "—"}</code>
          </div>
        </div>
      )}
    </section>
  );
}
